import React, { useState, useCallback, useMemo } from 'react'
import {
  Button, Input, Switch, Space, Modal, Tag, Spin, App, Popconfirm, Row, Col, Card
} from 'antd'
import {
  SettingOutlined, CheckCircleOutlined, CloseCircleOutlined, LockOutlined
} from '@ant-design/icons'
import { useI18n } from '@/shared/i18n'
import { useCurrentCompany } from '@/stores/authStore'
import { api } from '@/shared/api/client'

interface ChannelCredentials {
  account_sid?: string
  auth_token?: string
  whatsapp_number?: string
  phone_number_id?: string
  access_token?: string
}

interface Channel {
  id: number
  platform: string
  platform_account: string
  credentials: ChannelCredentials
  is_active: boolean
}

interface ChannelForm {
  platform: string
  platform_account: string
  credentials: ChannelCredentials
  is_active: boolean
}

const defaultForm = (platform: string): ChannelForm => ({
  platform,
  platform_account: '',
  credentials: {},
  is_active: true,
})

const WhatsAppChannelConfig: React.FC = () => {
  const { t } = useI18n('modules')
  const { t: tc } = useI18n('common')
  const { message } = App.useApp()
  const company = useCurrentCompany()
  const companyId = company?.id

  const [channels, setChannels] = useState<Channel[]>([])
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [activePlatform, setActivePlatform] = useState<string>('')
  const [form, setForm] = useState<ChannelForm>(defaultForm('twilio'))
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)

  const fetchChannels = useCallback(async () => {
    if (!companyId) return
    setLoading(true)
    try {
      const res = await api.get<{ channels: Channel[]; total: number }>(`/channels/${companyId}`)
      setChannels(res.channels || [])
    } catch {
      message.error(t('settings.channels.whatsapp.fetchError'))
    } finally {
      setLoading(false)
    }
  }, [companyId])

  React.useEffect(() => {
    if (companyId) fetchChannels()
  }, [companyId, fetchChannels])

  const getChannelByPlatform = (platform: string) =>
    channels.find(c => c.platform === platform)

  const openConfigure = (platform: string) => {
    const existing = getChannelByPlatform(platform)
    setActivePlatform(platform)
    if (existing) {
      setEditingId(existing.id)
      setForm({
        platform: existing.platform,
        platform_account: existing.platform_account,
        credentials: { ...existing.credentials },
        is_active: existing.is_active,
      })
    } else {
      setEditingId(null)
      setForm(defaultForm(platform))
    }
    setTestResult(null)
    setShowModal(true)
  }

  const handleSave = async () => {
    if (!companyId) return
    setSaving(true)
    try {
      if (editingId !== null) {
        await api.put(`/channels/${companyId}/${editingId}`, form)
      } else {
        await api.post(`/channels/${companyId}`, form)
      }
      message.success(t('settings.channels.whatsapp.saveSuccess'))
      setShowModal(false)
      await fetchChannels()
    } catch {
      message.error(t('settings.channels.whatsapp.saveError'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!companyId) return
    try {
      await api.delete(`/channels/${companyId}/${id}`)
      message.success(t('settings.channels.whatsapp.deleteSuccess'))
      fetchChannels()
    } catch {
      message.error(t('settings.channels.whatsapp.deleteError'))
    }
  }

  const handleTest = async () => {
    if (!companyId) return
    setTesting(true)
    setTestResult(null)
    try {
      const res = await api.post<{ success: boolean; message?: string }>(`/channels/${companyId}/test`, form)
      setTestResult({ success: res.success, message: res.message || '' })
    } catch {
      setTestResult({ success: false, message: t('settings.channels.whatsapp.testError') })
    } finally {
      setTesting(false)
    }
  }

  const statusTag = (platform: string) => {
    const ch = getChannelByPlatform(platform)
    if (!ch) return <Tag color="default">{t('settings.channels.noRecord')}</Tag>
    if (!ch.is_active) return <Tag color="default">{t('settings.channels.inactive')}</Tag>
    if (ch.platform_account) return <Tag color="green" icon={<CheckCircleOutlined />}>{t('settings.channels.configured')}</Tag>
    return <Tag color="orange" icon={<CloseCircleOutlined />}>{t('settings.channels.notConfigured')}</Tag>
  }

  const CARDS = useMemo(() => [
    {
      platform: 'twilio',
      name: t('settings.channels.twilio.name'),
      desc: t('settings.channels.twilio.desc'),
      comingSoon: false,
    },
    {
      platform: 'whatsapp',
      name: t('settings.channels.meta.name'),
      desc: t('settings.channels.meta.desc'),
      comingSoon: false,
    },
    {
      platform: 'aliyun_whatsapp',
      name: t('settings.channels.aliyun.name'),
      desc: t('settings.channels.aliyun.desc'),
      comingSoon: true,
    },
  ], [t])

  const modalTitle = activePlatform === 'twilio'
    ? t('settings.channels.twilio.name')
    : activePlatform === 'whatsapp'
      ? t('settings.channels.meta.name')
      : t('settings.channels.aliyun.name')

  return (
    <div>
      <h3 style={{ marginBottom: 16 }}>{t('settings.channels.title')}</h3>
      <Spin spinning={loading}>
        <Row gutter={16}>
          {CARDS.map(card => {
            const existingChannel = getChannelByPlatform(card.platform)
            return (
              <Col xs={24} sm={8} key={card.platform}>
                <Card
                  style={{ height: '100%', opacity: card.comingSoon ? 0.6 : 1 }}
                  styles={{ body: { display: 'flex', flexDirection: 'column', gap: 8 } }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <strong>{card.name}</strong>
                    {card.comingSoon
                      ? <Tag icon={<LockOutlined />} color="default">{t('settings.channels.aliyun.comingSoon')}</Tag>
                      : statusTag(card.platform)
                    }
                  </div>
                  <div style={{ color: '#666', fontSize: 12, flex: 1 }}>{card.desc}</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                      size="small"
                      icon={<SettingOutlined />}
                      disabled={card.comingSoon}
                      onClick={() => openConfigure(card.platform)}
                    >
                      {t('settings.channels.configure')}
                    </Button>
                    {!card.comingSoon && existingChannel && (
                      <Popconfirm
                        title={t('settings.channels.whatsapp.deleteConfirm')}
                        onConfirm={() => handleDelete(existingChannel.id)}
                        okText={tc('confirm')}
                        cancelText={tc('cancel')}
                      >
                        <Button size="small" danger>{tc('delete')}</Button>
                      </Popconfirm>
                    )}
                  </div>
                </Card>
              </Col>
            )
          })}
        </Row>
      </Spin>

      <Modal
        title={modalTitle}
        open={showModal}
        onCancel={() => setShowModal(false)}
        footer={
          <Space>
            <Button onClick={handleTest} loading={testing}>
              {t('settings.channels.whatsapp.testConnection')}
            </Button>
            <Button onClick={() => setShowModal(false)}>{tc('cancel')}</Button>
            <Button type="primary" onClick={handleSave} loading={saving}>{tc('save')}</Button>
          </Space>
        }
        width={520}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
          {activePlatform === 'twilio' && (
            <>
              <div>
                <label>{t('settings.channels.whatsapp.accountSid')}</label>
                <Input
                  style={{ marginTop: 4 }}
                  value={form.credentials.account_sid || ''}
                  onChange={e => setForm(f => ({ ...f, credentials: { ...f.credentials, account_sid: e.target.value } }))}
                  placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                />
              </div>
              <div>
                <label>{t('settings.channels.whatsapp.authToken')}</label>
                <Input.Password
                  style={{ marginTop: 4 }}
                  value={form.credentials.auth_token || ''}
                  onChange={e => setForm(f => ({ ...f, credentials: { ...f.credentials, auth_token: e.target.value } }))}
                  placeholder={t('settings.channels.whatsapp.authTokenPlaceholder')}
                />
              </div>
              <div>
                <label>{t('settings.channels.whatsapp.whatsappNumber')}</label>
                <Input
                  style={{ marginTop: 4 }}
                  value={form.credentials.whatsapp_number || form.platform_account}
                  onChange={e => setForm(f => ({
                    ...f,
                    platform_account: e.target.value,
                    credentials: { ...f.credentials, whatsapp_number: e.target.value }
                  }))}
                  placeholder="+14155238886"
                />
              </div>
            </>
          )}

          {activePlatform === 'whatsapp' && (
            <>
              <div>
                <label>{t('settings.channels.meta.phoneNumberId')}</label>
                <Input
                  style={{ marginTop: 4 }}
                  value={form.platform_account}
                  onChange={e => setForm(f => ({ ...f, platform_account: e.target.value }))}
                  placeholder="123456789012345"
                />
              </div>
              <div>
                <label>{t('settings.channels.meta.accessToken')}</label>
                <Input.Password
                  style={{ marginTop: 4 }}
                  value={form.credentials.access_token || ''}
                  onChange={e => setForm(f => ({ ...f, credentials: { ...f.credentials, access_token: e.target.value } }))}
                  placeholder={t('settings.channels.meta.accessTokenPlaceholder')}
                />
              </div>
            </>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label>{t('settings.channels.whatsapp.active')}</label>
            <Switch
              checked={form.is_active}
              onChange={v => setForm(f => ({ ...f, is_active: v }))}
            />
          </div>

          {testResult && (
            <div style={{
              padding: '8px 12px', borderRadius: 6,
              background: testResult.success ? '#f6ffed' : '#fff2f0',
              border: `1px solid ${testResult.success ? '#b7eb8f' : '#ffccc7'}`,
              color: testResult.success ? '#52c41a' : '#ff4d4f'
            }}>
              {testResult.success
                ? <CheckCircleOutlined style={{ marginRight: 6 }} />
                : <CloseCircleOutlined style={{ marginRight: 6 }} />
              }
              {testResult.message}
            </div>
          )}
        </div>
      </Modal>
    </div>
  )
}

export default WhatsAppChannelConfig
